import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { CloudStratum, isInkInverted } from "@/components/fit/CloudStratum";
import { FitQuestion } from "@/components/fit/FitQuestion";
import { FitResult } from "@/components/fit/FitResult";
import {
  QUESTION_COUNT,
  getQuestions,
  type AnswerKey,
  type Answers,
} from "@/components/fit/questions";
import { startViewTransition } from "@/lib/view-transition";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";

/**
 * FitPage — "Are we a good fit?" interactive Q&A.
 *
 * Renders the 4-question form over the cinematic CloudStratum backdrop.
 * Each step shows a question + answer pills; a one-line reinforcement
 * microcopy fades in under the selected pill. The user clicks Continue to
 * advance (the click triggers the cloud burst + card-approach animation).
 *
 * The final step computes a recommended tier (Audit / Core / Premium) from
 * the budget answer and surfaces three actions: book a Cal call (with the
 * answers pre-filled into the booking notes), request a proposal via
 * email, or read how-we-work without committing.
 */

const TOTAL_STEPS = QUESTION_COUNT + 1; // 4 questions + 1 result

export function FitPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const localizedHref = useLocalizedHref();
  const [step, setStep] = React.useState(0);
  const [answers, setAnswers] = React.useState<Answers>({});

  const questions = getQuestions(t);
  const inverted = isInkInverted(step);
  const isResult = step === TOTAL_STEPS - 1;
  const currentQ = isResult ? null : questions[step]!;
  const currentAnswer = currentQ ? answers[currentQ.key] : undefined;
  const selectedOption = currentQ?.options.find((o) => o.value === currentAnswer);

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  // On step 0 the user has nowhere to go "back" within the form, so Back
  // exits to the landing page instead of being disabled. Wrapped in
  // startViewTransition so the sky scene cross-fades into the landing
  // page (matching the Hero → /fit transition in the other direction).
  const prev = () => {
    if (step === 0) {
      startViewTransition(async () => {
        await navigate({ to: localizedHref("/") });
      });
      return;
    }
    setStep((s) => Math.max(s - 1, 0));
  };
  const select = (key: AnswerKey, value: string) => setAnswers((a) => ({ ...a, [key]: value }));

  // Smooth ink/border colour transitions when the sky inverts at altitude.
  const fadeColor = "transition-colors duration-[1800ms]";

  return (
    <div
      className={cn(
        "page relative min-h-[100svh] overflow-hidden",
        fadeColor,
        inverted ? "text-page-bg" : "text-ink",
      )}
    >
      <CloudStratum step={step} />

      {/* Top bar — brand link + step counter. */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-8 lg:px-10">
        <Link
          to={localizedHref("/")}
          className={cn("wordmark text-[18px] transition-opacity hover:opacity-80", fadeColor)}
          // .wordmark hardcodes `color: var(--color-ink)` — override on
          // inverted (night-sky) steps so "holy" doesn't disappear into
          // the dark backdrop. The `<em>moly</em>` keeps its accent
          // (orange) color from `.wordmark em`.
          style={{
            color: inverted ? "var(--color-page-bg)" : undefined,
          }}
        >
          holy<em>moly</em>
        </Link>
        <div
          className={cn(
            "mono text-[11px] tracking-[0.18em] uppercase",
            fadeColor,
            inverted ? "text-page-bg/70" : "text-ink-3",
          )}
        >
          {!isResult
            ? t("fit.stepLabel", { current: step + 1, total: QUESTION_COUNT })
            : t("fit.resultLabel")}
        </div>
      </div>

      {/* Centered card. Keyed on step so React unmounts the previous card
          and mounts a fresh one — the fresh mount triggers the
          card-approach animation in sync with the cloud burst. */}
      <div className="relative z-10 grid min-h-[calc(100svh-180px)] place-items-center px-6 pb-16">
        <div key={step} className="animate-card-approach w-full max-w-[760px] text-center">
          {isResult ? (
            <FitResult answers={answers} inverted={inverted} fadeColor={fadeColor} onBack={prev} />
          ) : (
            <FitQuestion
              question={currentQ!}
              stepIdx={step}
              selectedValue={currentAnswer}
              echo={selectedOption?.echo}
              inverted={inverted}
              fadeColor={fadeColor}
              onSelect={(v) => select(currentQ!.key, v)}
              onBack={prev}
              onNext={next}
              canBack={true}
              canNext={Boolean(currentAnswer)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
